/**
 * Zod schemas for ward-roster management (V2-E2, HAN-84). The single source of
 * truth for the *shape and rules* of the add-member / change-role payloads,
 * shared so the ward-admin web UI (E2.2) and the API validate identically — the
 * API's class-validator DTOs mirror these rules field-for-field, and the
 * constants below are imported by both sides so they can't drift.
 *
 * Kept dependency-light: only `zod`. No imports from the API or drizzle.
 */
import { z } from "zod";

/**
 * Membership roles on a ward — mirrors the `ward_member_role` Postgres enum
 * (design §3). `member` is the v1-equivalent (can use the ward); `admin` can
 * manage the ward's roster. Orthogonal to a user's *clinical* role
 * (consultant/registrar/…), which drives escalation, not access.
 */
export const WARD_MEMBER_ROLES = ["member", "admin"] as const;
export type WardMemberRoleInput = (typeof WARD_MEMBER_ROLES)[number];

/**
 * Status of a roster entry. E2.1 only ever produces `active` (a real
 * membership row); E2.2 adds `invited` for pending email invitations that
 * share the same roster list. Declared here so the list shape is stable across
 * both epics.
 */
export const MEMBER_STATUSES = ["active", "invited"] as const;
export type MemberStatus = (typeof MEMBER_STATUSES)[number];

/** GMC reference number: exactly 7 digits (mirrors the register DTO rule). */
export const GMC_NUMBER_PATTERN = /^\d{7}$/;

const email = z.string().trim().email("Enter a valid email address").max(254);
const gmcNumber = z
  .string()
  .trim()
  .regex(GMC_NUMBER_PATTERN, "GMC number must be exactly 7 digits");

/**
 * Payload for `POST /wards/:wardId/members` — add an **already-registered**
 * user to the ward. The caller identifies the user by *exactly one* of email
 * or GMC number (a single "email or GMC" input on the web form); supplying
 * both, or neither, is rejected so the lookup is unambiguous. If no such user
 * exists the API answers 404 and the caller falls back to an email invite
 * (E2.2).
 */
export const addMemberSchema = z
  .object({
    email: email.optional(),
    gmcNumber: gmcNumber.optional(),
  })
  .refine((value) => (value.email === undefined) !== (value.gmcNumber === undefined), {
    message: "Provide exactly one of an email address or a GMC number",
  });

export type AddMemberInput = z.infer<typeof addMemberSchema>;

/** Payload for `PATCH /wards/:wardId/members/:userId` — set the membership role. */
export const updateMemberRoleSchema = z.object({
  role: z.enum(WARD_MEMBER_ROLES),
});

export type UpdateMemberRoleInput = z.infer<typeof updateMemberRoleSchema>;
